import { CourseValue } from "../ConstructorCourse";
import pdf from "../../../../../../assets/images/pdf.svg";
import { Box, Button, TextField } from "@mui/material";
import PlusIcon from "@mui/icons-material/Add";
import React from "react";
import { Formik, Field, Form, FieldArray } from "formik";
import * as Yup from "yup";

interface ConstructorStepOneProps {
  courseValue: CourseValue;
  onDataChange: (data: CourseValue) => void;
  saveDraft: (data: CourseValue, b?: boolean) => void;
  handleOpenPreview: () => void;
}

const validationSchema = Yup.object().shape({
  courseGoals: Yup.array()
    .of(Yup.string().required("Необходимо ввести цель"))
    .min(4, "Необходимо ввести не меньше 4 целей"),
  courseRequirements: Yup.array()
    .of(Yup.string().required("Необходимо ввести требование"))
    .min(1, "Необходимо ввести хотя бы одно требование"),
  courseAuditory: Yup.array()
    .of(Yup.string().required("Необходимо ввести аудиторию"))
    .min(1, "Необходимо ввести хотя бы одну аудиторию"),
});

const ConstructorStepOne = ({
  courseValue,
  onDataChange,
  saveDraft,
}: ConstructorStepOneProps) => {
  return (
    <Box>
      <Formik
        initialValues={{
          courseGoals: courseValue.courseGoals,
          courseRequirements: courseValue.courseRequirements,
          courseAuditory: courseValue.courseAuditory,
        }}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={(values: CourseValue) => {
          onDataChange(values);
          saveDraft(values);
        }}>
        {({ values }) => (
          <Form>
            <div className="course-content">
              <div className="constructor-content-title">
                Рекомендации по созданию курса
              </div>
              <div className="corse-content-description">
                В данном файле вы можете ознакомиться с рекомендациями и
                советами как создать эффективный курс
              </div>
              <div className="course-recommendations">
                <img src={pdf} alt="Recommendations.pdf" />
                <div className="file-name">Рекомендации и советы.pdf</div>
                <div className="download">Скачать</div>
              </div>

              <div className="constructor-content-title">
                Что студенты будут изучать на вашем курсе?{" "}
                <span>обязательно</span>
              </div>
              <div className="corse-content-description">
                Необходимо ввести не меньше 4 целей обучения или итогов, на
                которые учащиеся могут рассчитывать после завершения вашего
                курса. Вводите каждый критерий отдельно.
              </div>
              <FieldArray name="courseGoals">
                {({ push, remove, form }) => (
                  <>
                    {form.values.courseGoals.map(
                      (goal: string, index: number) => (
                        <div key={index}>
                          <Field
                            name={`courseGoals.${index}`}
                            as={TextField}
                            variant="outlined"
                            className="without-label"
                            placeholder="Пример: оценка бюджета"
                            sx={{
                              marginTop: "12px",
                              width: 600,
                              "@media (max-width:1000px)": {
                                width: "100%",
                              },
                            }}
                            error={Boolean(
                              form.touched.courseGoals &&
                                form.errors.courseGoals &&
                                (form.errors.courseGoals as string[])[index]
                            )}
                            helperText={
                              form.touched.courseGoals &&
                              form.errors.courseGoals &&
                              (form.errors.courseGoals as string[])[index]
                            }
                          />
                          {index >= 4 && (
                            <Button
                              type="button"
                              onClick={() => remove(index)}
                              variant="text"
                              sx={{
                                color: "#0573E4",
                                fontSize: "16px",
                                fontWeight: "600",
                                marginTop: "18px",
                                textTransform: "none",
                              }}>
                              Удалить
                            </Button>
                          )}
                        </div>
                      )
                    )}
                    <Button
                      type="button"
                      onClick={() => push("")}
                      variant="text"
                      sx={{
                        color: "#0573E4",
                        fontSize: "16px",
                        fontWeight: "600",
                        marginTop: "18px",
                        marginBottom: "52px",
                        textTransform: "none",
                      }}>
                      <PlusIcon /> Добавить
                    </Button>
                  </>
                )}
              </FieldArray>
              <div className="constructor-content-title">
                Есть ли у вашего курса какие-либо требования?{" "}
                <span>обязательно</span>
              </div>
              <div className="corse-content-description">
                Список необходимых навыков, знаний, инструментов и оборудования,
                которыми должны владеть учащиеся до старта вашего курса. Если у
                вас нет особых требований, используйте данное поле, чтобы
                привлечь больше начинающих учащихся.
              </div>
              <FieldArray name="courseRequirements">
                {({ push, remove, form }) => (
                  <>
                    {form.values.courseRequirements.map(
                      (requirement: string, index: number) => (
                        <div key={index}>
                          <Field
                            name={`courseRequirements.${index}`}
                            as={TextField}
                            className="without-label"
                            variant="outlined"
                            placeholder="Пример: базовый уровень ООП"
                            sx={{ marginTop: "12px", width: "100%" }}
                            error={Boolean(
                              form.touched.courseRequirements &&
                                form.errors.courseRequirements &&
                                (form.errors.courseRequirements as string[])[
                                  index
                                ]
                            )}
                            helperText={
                              form.touched.courseRequirements &&
                              form.errors.courseRequirements &&
                              (form.errors.courseRequirements as string[])[
                                index
                              ]
                            }
                          />
                          {index >= 1 && (
                            <Button
                              type="button"
                              onClick={() => remove(index)}
                              variant="text"
                              sx={{
                                color: "#0573E4",
                                fontSize: "16px",
                                fontWeight: "600",
                                marginTop: "18px",
                                textTransform: "none",
                              }}>
                              Удалить
                            </Button>
                          )}
                        </div>
                      )
                    )}
                    <Button
                      type="button"
                      onClick={() => push("")}
                      variant="text"
                      sx={{
                        color: "#0573E4",
                        fontSize: "16px",
                        fontWeight: "600",
                        marginTop: "18px",
                        marginBottom: "52px",
                        textTransform: "none",
                      }}>
                      <PlusIcon /> Дополнить
                    </Button>
                  </>
                )}
              </FieldArray>
              <div className="constructor-content-title">
                Для кого предназначен этот курс? <span>обязательно</span>
              </div>
              <div className="corse-content-description">
                Составьте подробное описание целевых учащихся вашего курса,
                которым были бы полезны ваши материалы. Это поможет вам привлечь
                правильную аудиторию для курса.
              </div>
              <FieldArray name="courseAuditory">
                {({ push, remove, form }) => (
                  <>
                    {form.values.courseAuditory.map(
                      (audience: string, index: number) => (
                        <div key={index}>
                          <Field
                            name={`courseAuditory.${index}`}
                            as={TextField}
                            className="without-label"
                            variant="outlined"
                            placeholder="Пример: начинающие дизайнеры"
                            sx={{ marginTop: "12px", width: "100%" }}
                            error={Boolean(
                              form.touched.courseAuditory &&
                                form.errors.courseAuditory &&
                                (form.errors.courseAuditory as string[])[index]
                            )}
                            helperText={
                              form.touched.courseAuditory &&
                              form.errors.courseAuditory &&
                              (form.errors.courseAuditory as string[])[index]
                            }
                          />
                          {index >= 1 && (
                            <Button
                              type="button"
                              onClick={() => remove(index)}
                              variant="text"
                              sx={{
                                color: "#0573E4",
                                fontSize: "16px",
                                fontWeight: "600",
                                marginTop: "18px",
                                textTransform: "none",
                              }}>
                              Удалить
                            </Button>
                          )}
                        </div>
                      )
                    )}
                    <Button
                      type="button"
                      onClick={() => push("")}
                      variant="text"
                      sx={{
                        color: "#0573E4",
                        fontSize: "16px",
                        fontWeight: "600",
                        marginTop: "18px",
                        marginBottom: "52px",
                        textTransform: "none",
                      }}>
                      <PlusIcon /> Дополнить
                    </Button>
                  </>
                )}
              </FieldArray>
            </div>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#F5F8FB",
                border: "1px solid #E5E5E5",
                padding: "12px 32px 12px 32px",
                marginTop: "52px",
                "@media (max-width:1000px)": {
                  backgroundColor: "transparent",
                  border: "none",
                  width: "100%",
                  padding: 0,
                },
              }}>
              <Box
                sx={{
                  display: "flex",
                  gap: "12px",
                  marginLeft: "auto",
                  "@media (max-width:1000px)": {
                    margin: 0,
                    width: "100%",
                    justifyContent: "space-between",
                  },
                }}>
                <Button
                  type="submit"
                  onClick={() => saveDraft(values, true)}
                  variant="outlined"
                  sx={{
                    "@media (max-width:1000px)": {
                      width: "48%",
                    },
                  }}>
                  Сохранить как черновик
                </Button>

                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    "@media (max-width:1000px)": {
                      width: "48%",
                    },
                  }}>
                  Далее
                </Button>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ConstructorStepOne;
